import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import BackgroundGradient from "@/components/landing/BackgroundGradient";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import { supabase } from "@/integrations/supabase/client";
import { onFormSubmitted } from "@/lib/automations";
import { sendEmail } from "@/lib/emailService";
import { FormInput } from "@/components/forms/FormInput";
import { FormDropdown, type DropdownOption } from "@/components/forms/FormDropdown";
import { FormSection } from "@/components/forms/FormSection";
import PhoneInput from "@/components/forms/PhoneInput";
import FileUpload, { type UploadedFile } from "@/components/forms/FileUpload";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";

const CALENDLY_URL =
  import.meta.env.VITE_BOOKING_LINK || "https://calendly.com/pinch-dyna-agency/30min";

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_MULTI = 3;

type Step = 1 | 2 | 3 | 4 | 5;

// ── Option sets ───────────────────────────────────────────────────────────────

const opts = {
  preferredContactEN: [
    { value: "", label: "Select…" },
    { value: "Email", label: "Email" },
    { value: "Phone", label: "Phone" },
    { value: "WhatsApp", label: "WhatsApp" },
    { value: "Any", label: "Any" },
  ] as DropdownOption[],
  preferredContactHE: [
    { value: "", label: "בחר/י…" },
    { value: "Email", label: "אימייל" },
    { value: "Phone", label: "טלפון" },
    { value: "WhatsApp", label: "וואטסאפ" },
    { value: "Any", label: "לא משנה" },
  ] as DropdownOption[],
  marketEN: [
    { value: "", label: "Select…" },
    { value: "Israel", label: "Israel" },
    { value: "United States", label: "United States" },
    { value: "Both", label: "Both" },
  ] as DropdownOption[],
  marketHE: [
    { value: "", label: "בחר/י…" },
    { value: "Israel", label: "ישראל" },
    { value: "United States", label: "ארצות הברית" },
    { value: "Both", label: "שניהם" },
  ] as DropdownOption[],
  stageEN: [
    { value: "", label: "Select…" },
    { value: "Just starting", label: "Just starting" },
    { value: "Under 1 year", label: "Under 1 year" },
    { value: "1–3 years", label: "1–3 years" },
    { value: "3+ years", label: "3+ years" },
  ] as DropdownOption[],
  stageHE: [
    { value: "", label: "בחר/י…" },
    { value: "Just starting", label: "בתחילת הדרך" },
    { value: "Under 1 year", label: "פחות משנה" },
    { value: "1–3 years", label: "1–3 שנים" },
    { value: "3+ years", label: "3+ שנים" },
  ] as DropdownOption[],
  serviceEN: [
    { value: "", label: "Select…" },
    { value: "Web Design", label: "Web Design" },
    { value: "SEO & GEO", label: "SEO & GEO" },
    { value: "Both", label: "Both" },
    { value: "Not sure yet", label: "Not sure yet" },
  ] as DropdownOption[],
  serviceHE: [
    { value: "", label: "בחר/י…" },
    { value: "Web Design", label: "עיצוב אתר" },
    { value: "SEO & GEO", label: "SEO ו-GEO" },
    { value: "Both", label: "גם וגם" },
    { value: "Not sure yet", label: "עוד לא בטוח/ה" },
  ] as DropdownOption[],
  budgetEN: [
    { value: "", label: "Select…" },
    { value: "Under $500/month", label: "Under $500/month" },
    { value: "$500–$1,500/month", label: "$500–$1,500/month" },
    { value: "$1,500–$3,000/month", label: "$1,500–$3,000/month" },
    { value: "$3,000+/month", label: "$3,000+/month" },
    { value: "One-time project", label: "One-time project" },
    { value: "Not sure yet", label: "Not sure yet" },
  ] as DropdownOption[],
  budgetHE: [
    { value: "", label: "בחר/י…" },
    { value: "Under $500/month", label: "עד ₪1,800/חודש ($500)" },
    { value: "$500–$1,500/month", label: "₪1,800–₪5,400/חודש" },
    { value: "$1,500–$3,000/month", label: "₪5,400–₪11,000/חודש" },
    { value: "$3,000+/month", label: "₪11,000+/חודש" },
    { value: "One-time project", label: "פרויקט חד-פעמי" },
    { value: "Not sure yet", label: "עוד לא בטוח/ה" },
  ] as DropdownOption[],
  timelineEN: [
    { value: "", label: "Select…" },
    { value: "ASAP", label: "ASAP" },
    { value: "Within 1 month", label: "Within 1 month" },
    { value: "1–3 months", label: "1–3 months" },
    { value: "Just exploring", label: "Just exploring" },
  ] as DropdownOption[],
  timelineHE: [
    { value: "", label: "בחר/י…" },
    { value: "ASAP", label: "בהקדם האפשרי" },
    { value: "Within 1 month", label: "תוך חודש" },
    { value: "1–3 months", label: "1–3 חודשים" },
    { value: "Just exploring", label: "רק בודק/ת אפשרויות" },
  ] as DropdownOption[],
  agencyEN: [
    { value: "", label: "Select…" },
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ] as DropdownOption[],
  agencyHE: [
    { value: "", label: "בחר/י…" },
    { value: "Yes", label: "כן" },
    { value: "No", label: "לא" },
  ] as DropdownOption[],
  referralEN: [
    { value: "", label: "Select…" },
    { value: "Google", label: "Google" },
    { value: "Instagram", label: "Instagram" },
    { value: "LinkedIn", label: "LinkedIn" },
    { value: "Facebook", label: "Facebook" },
    { value: "Referral", label: "Referral / word of mouth" },
  ] as DropdownOption[],
  referralHE: [
    { value: "", label: "בחר/י…" },
    { value: "Google", label: "גוגל" },
    { value: "Instagram", label: "אינסטגרם" },
    { value: "LinkedIn", label: "לינקדאין" },
    { value: "Facebook", label: "פייסבוק" },
    { value: "Referral", label: "המלצה / מפה לאוזן" },
  ] as DropdownOption[],
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const resolve = (val: string, other: string) => (val === "Other" ? other : val) || null;

// ── Module-level sub-components ────────────────────────────────────────────────
// These MUST be defined outside the render function — defining them inside causes
// React to treat each render as a new component type, unmounting/remounting their
// inputs and dismissing the mobile keyboard after every keystroke.

const ProgressBar = memo(function ProgressBar({ step }: { step: Step }) {
  return (
    <div className="book-form__progress">
      {([1, 2, 3, 4, 5] as const).map(n => (
        <div
          key={n}
          className={`book-form__progress-seg${n <= step ? " is-active" : ""}`}
        />
      ))}
    </div>
  );
});

const Heading = memo(function Heading({
  text,
  align,
}: {
  text: string;
  align: "left" | "right";
}) {
  return (
    <h1 className="book-form__heading" style={{ textAlign: align }}>
      {text}
    </h1>
  );
});

const NavButtons = memo(function NavButtons({
  step,
  canNext,
  submitting,
  isSubmit,
  onBack,
  onNext,
  rtl,
}: {
  step: Step;
  canNext: boolean;
  submitting: boolean;
  isSubmit?: boolean;
  onBack: () => void;
  onNext: () => void;
  rtl: boolean;
}) {
  const backLabel = rtl ? "→ חזרה" : "← Back";
  const nextLabel = rtl ? "המשך ←" : "Next →";
  const submitLabel = submitting ? (rtl ? "שולח…" : "Sending…") : (rtl ? "שלח ←" : "Submit →");
  const disabled = !canNext || submitting;
  return (
    <div className="book-form__nav">
      {step > 1 ? (
        <button className="dyna-btn dyna-btn--secondary" onClick={onBack}>
          {backLabel}
        </button>
      ) : <div />}
      <button
        className="dyna-btn dyna-btn--primary"
        disabled={disabled}
        style={{ opacity: disabled ? 0.5 : 1, cursor: !canNext ? "not-allowed" : "pointer" }}
        onClick={onNext}
      >
        {isSubmit ? submitLabel : nextLabel}
      </button>
    </div>
  );
});

const MultiField = memo(function MultiField({
  label,
  values,
  setValues,
  type = "text",
  addLabel,
  placeholder,
  autoComplete,
}: {
  label: string;
  values: string[];
  setValues: React.Dispatch<React.SetStateAction<string[]>>;
  type?: "text" | "email" | "tel";
  addLabel: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div className="dyna-modal__field">
      <label className="dyna-modal__label">{label}</label>
      <div className="book-form__multi">
        {values.map((v, i) => (
          <div key={`multi-${i}`} className="book-form__multi-row">
            {type === "tel" ? (
              <div style={{ flex: 1 }}>
                <PhoneInput
                  value={v}
                  onChange={val => setValues(prev => prev.map((x, idx) => (idx === i ? val : x)))}
                  placeholder={placeholder}
                  autoComplete={autoComplete}
                />
              </div>
            ) : (
              <input
                className="dyna-modal__input"
                type={type}
                value={v}
                onChange={e => {
                  const val = e.target.value;
                  setValues(prev => prev.map((x, idx) => (idx === i ? val : x)));
                }}
                placeholder={placeholder}
                dir="ltr"
                autoComplete={autoComplete}
              />
            )}
            {values.length > 1 && (
              <button
                type="button"
                className="book-form__remove-btn"
                onClick={() => setValues(prev => prev.filter((_, idx) => idx !== i))}
                aria-label="Remove"
              >
                ✕
              </button>
            )}
          </div>
        ))}
        {values.length < MAX_MULTI && (
          <button
            type="button"
            className="book-form__add-btn"
            onClick={() =>
              setValues(prev => (prev.length < MAX_MULTI ? [...prev, ""] : prev))
            }
          >
            + {addLabel}
          </button>
        )}
      </div>
    </div>
  );
});

// ── Main component ─────────────────────────────────────────────────────────────

export default function BookPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const rtl = i18n.language === "he";
  const dir = rtl ? "rtl" : "ltr";
  const T = <E extends string, H extends string>(en: E, he: H): string => (rtl ? he : en);

  const prefillTier = searchParams.get("tier");
  const prefillNiche = searchParams.get("niche");
  const prefillTierLabel = prefillTier
    ? { spark: T("Spark", "ניצוץ"), launch: T("Launch", "שיגור"), backbone: T("Backbone", "עמוד שדרה") }[prefillTier as "spark" | "launch" | "backbone"] ?? null
    : null;

  // ── Step ──────────────────────────────────────────────────────────────────

  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [leadId, setLeadId] = useState<string | null>(null);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);

  // ── Section 1: Personal Details ──────────────────────────────────────────

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emails, setEmails] = useState([""]);
  const [phones, setPhones] = useState([""]);
  const [whatsapps, setWhatsapps] = useState([""]);
  const [waSameAsPhone, setWaSameAsPhone] = useState(false);
  const [preferredContact, setPreferredContact] = useState("");
  const [preferredContactOther, setPreferredContactOther] = useState("");

  // ── Section 2: Your Business ─────────────────────────────────────────────

  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [market, setMarket] = useState("");
  const [marketOther, setMarketOther] = useState("");
  const [businessStage, setBusinessStage] = useState("");
  const [businessStageOther, setBusinessStageOther] = useState("");

  // ── Section 3: What You Need ─────────────────────────────────────────────

  const [serviceInterest, setServiceInterest] = useState("");
  const [serviceInterestOther, setServiceInterestOther] = useState("");
  const [mainGoal, setMainGoal] = useState("");
  const [budget, setBudget] = useState("");
  const [budgetOther, setBudgetOther] = useState("");
  const [timeline, setTimeline] = useState("");
  const [timelineOther, setTimelineOther] = useState("");
  const [workedWithAgency, setWorkedWithAgency] = useState("");
  const [workedWithAgencyOther, setWorkedWithAgencyOther] = useState("");

  // ── Section 4: How You Found Us ──────────────────────────────────────────

  const [referralSource, setReferralSource] = useState("");
  const [referralSourceOther, setReferralSourceOther] = useState("");
  const [referralName, setReferralName] = useState("");
  const [anythingElse, setAnythingElse] = useState("");

  // ── Section 5: Files ─────────────────────────────────────────────────────

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const effectiveWhatsapps = waSameAsPhone ? phones : whatsapps;

  // ── Validation ────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    setError("");
    if (step === 1) {
      if (!firstName.trim()) { setError(T("First name is required.", "שם פרטי הוא שדה חובה.")); return false; }
      if (!lastName.trim()) { setError(T("Last name is required.", "שם משפחה הוא שדה חובה.")); return false; }
      if (!emails[0].trim()) { setError(T("Email address is required.", "כתובת אימייל היא שדה חובה.")); return false; }
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRe.test(emails[0])) { setError(T("Please enter a valid email address.", "נא להזין כתובת אימייל תקינה.")); return false; }
    }
    if (step === 2) {
      if (!businessName.trim()) { setError(T("Business name is required.", "שם העסק הוא שדה חובה.")); return false; }
      if (!businessDescription.trim()) { setError(T("Business description is required.", "תיאור העסק הוא שדה חובה.")); return false; }
    }
    if (step === 3) {
      if (!mainGoal.trim()) { setError(T("Main goal is required.", "מטרה עיקרית היא שדה חובה.")); return false; }
    }
    return true;
  };

  const canNext = (): boolean => {
    if (step === 1) return !!(firstName.trim() && lastName.trim() && emails[0].trim());
    if (step === 2) return !!(businessName.trim() && businessDescription.trim());
    if (step === 3) return !!mainGoal.trim();
    return true;
  };

  const goBack = () => setStep(s => (s - 1) as Step);
  const goNext = () => {
    if (!validate()) return;
    // Transitioning from step 4 → step 5 submits the lead so Calendly can
    // be prefilled and the booking record can be tied back to a real lead_id.
    if (step === 4 && !leadId) {
      void handleSubmit();
      return;
    }
    setStep(s => (s + 1) as Step);
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setError("");

    const language = rtl ? "he" : "en";
    const cleanEmails = emails.filter(Boolean);
    const cleanPhones = phones.filter(Boolean);
    const cleanWhatsapps = effectiveWhatsapps.filter(Boolean);

    const leadPayload: Record<string, unknown> = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: cleanEmails[0],
      emails: cleanEmails,
      phone: cleanPhones[0] ?? null,
      phones: cleanPhones,
      whatsapp: cleanWhatsapps,
      preferred_contact: resolve(preferredContact, preferredContactOther),
      business_name: businessName.trim(),
      business_description: businessDescription.trim(),
      website_url: websiteUrl.trim() || null,
      market: resolve(market, marketOther),
      business_stage: resolve(businessStage, businessStageOther),
      service_interest: resolve(serviceInterest, serviceInterestOther),
      main_goal: mainGoal.trim() || null,
      budget_range: resolve(budget, budgetOther),
      timeline: resolve(timeline, timelineOther),
      worked_with_agency: resolve(workedWithAgency, workedWithAgencyOther),
      referral_source: resolve(referralSource, referralSourceOther),
      referral_name: referralSource === "Referral" ? referralName.trim() || null : null,
      other_notes: anythingElse.trim() || null,
      source: prefillTier ? `book_from_pricing_${prefillTier}` : "website_form",
      status: "new",
      language,
      uploaded_files: uploadedFiles,
      ...(prefillTier ? { preferred_tier: prefillTier } : {}),
      ...(prefillNiche ? { niche: prefillNiche } : {}),
    };

    try {
      const { data: leadData, error: dbError } = await supabase
        .from("leads")
        .insert(leadPayload)
        .select("id")
        .single();

      if (dbError) {
        console.error("[BookPage] Lead insert failed:", dbError);
        console.error("[BookPage] Payload that failed:", leadPayload);
        throw dbError;
      }

      // Email is secondary — don't let a failure block form success.
      try {
        await onFormSubmitted({
          email: cleanEmails[0],
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          language,
          leadId: leadData?.id,
        });
      } catch (emailErr) {
        console.error("[BookPage] Email send failed (non-blocking):", emailErr);
      }

      if (leadData?.id) setLeadId(leadData.id);
      setStep(5);
      setSubmitting(false);
      return;
    } catch (err) {
      console.error("[BookPage] Form submission failed — full details:", err);
      console.error("[BookPage] Form data at time of error:", leadPayload);
      setError(T(
        "Something went wrong. Please try again or email hello@dyna-agency.com",
        "משהו השתבש. נסה/י שוב או שלח/י מייל ל-hello@dyna-agency.com"
      ));
    }

    setSubmitting(false);
  };

  // ── Calendly prefill (memoized so the embed doesn't re-init each render) ──

  const calendlyPrefill = useMemo(
    () => ({
      name: `${firstName.trim()} ${lastName.trim()}`.trim(),
      email: emails[0] || "",
      customAnswers: {
        a1: phones[0] || "",
        a2: businessName.trim(),
      },
    }),
    [firstName, lastName, emails, phones, businessName]
  );

  // ── Calendly event listener — insert booking when client picks a slot ────

  useEffect(() => {
    const handleCalendlyEvent = async (e: MessageEvent) => {
      // Calendly sends events from calendly.com domain
      if (!e.origin.includes("calendly.com")) return;

      // Log all Calendly events to see what's coming through
      console.log("[Calendly] Event received:", e.data);

      const data = e.data as
        | { event?: string; payload?: { event?: { uri?: string }; invitee?: { uri?: string } } }
        | undefined;
      if (!data || data.event !== "calendly.event_scheduled") return;
      console.log("[Calendly] Booking confirmed!", data.payload);

      const guestName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const guestEmail = emails[0] || "";

      try {
        const { error: insertErr } = await supabase.from("bookings").insert({
          lead_id: leadId || null,
          status: "scheduled",
          source: "calendly_inline",
          booking_date: new Date().toISOString(),
          guest_name: guestName || null,
          guest_email: guestEmail || null,
          form_data: {
            name: guestName,
            email: guestEmail,
            phone: phones[0] || "",
            business_name: businessName.trim(),
            event_uri: data.payload?.event?.uri || null,
            invitee_uri: data.payload?.invitee?.uri || null,
          },
        });

        if (insertErr) {
          console.error("[Calendly] Failed to save booking:", insertErr);
        } else {
          console.log("[Calendly] Booking saved to Supabase!");
        }
      } catch (err) {
        console.error("[Calendly] Booking save error:", err);
      }

      // Notify admin — non-blocking, never gates the success screen.
      try {
        await sendEmail({
          to: "hello@dyna-agency.com",
          templateId: "discoveryCallConfirmation",
          data: {
            first_name: firstName.trim(),
            business_name: businessName.trim(),
            email: guestEmail,
          },
          language: "en",
          leadId: leadId || undefined,
        });
      } catch (err) {
        console.error("[Calendly] Admin notification failed:", err);
      }

      // Show success screen regardless of save result
      setBookingConfirmed(true);
    };

    window.addEventListener("message", handleCalendlyEvent);
    return () => window.removeEventListener("message", handleCalendlyEvent);
  }, [leadId, firstName, lastName, emails, phones, businessName]);

  // ── Derived option sets ───────────────────────────────────────────────────

  const o = {
    preferredContact: rtl ? opts.preferredContactHE : opts.preferredContactEN,
    market: rtl ? opts.marketHE : opts.marketEN,
    stage: rtl ? opts.stageHE : opts.stageEN,
    service: rtl ? opts.serviceHE : opts.serviceEN,
    budget: rtl ? opts.budgetHE : opts.budgetEN,
    timeline: rtl ? opts.timelineHE : opts.timelineEN,
    agency: rtl ? opts.agencyHE : opts.agencyEN,
    referral: rtl ? opts.referralHE : opts.referralEN,
  };

  const headingAlign = rtl ? "right" : "left";

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <SEO
        title={T("Book a Call — Dyna Agency", "הזמן שיחה — Dyna Agency")}
        description={T(
          "Tell us about your project and book a free discovery call with Dyna Agency.",
          "ספרו לנו על הפרויקט שלכם והזמינו שיחת היכרות חינם עם Dyna Agency."
        )}
        path="/book"
      />
      <BackgroundGradient />
      <div dir={dir} style={{ minHeight: "100vh", color: "var(--dyna-fg)", position: "relative", zIndex: 1 }}>
        <Navbar />
        <main style={{ paddingTop: 100, paddingBottom: 80 }}>
          <div className="book-form">

            {prefillTierLabel && (
              <div className="book-form__prefill" role="status" aria-live="polite">
                <span className="book-form__prefill-eyebrow">{T("Selected plan", "תוכנית נבחרת")}</span>
                <strong className="book-form__prefill-tier">{prefillTierLabel}</strong>
                {prefillNiche && (
                  <span className="book-form__prefill-niche">{T("Niche", "תחום")}: {prefillNiche}</span>
                )}
              </div>
            )}

            <ProgressBar step={step} />

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >

                {/* ── STEP 1: Personal Details ────────────────────────── */}
                {step === 1 && (
                  <div className="book-form__card">
                    <FormSection label={T("Section 1 — Personal Details", "חלק 1 — פרטים אישיים")}>
                      <Heading text={T("Tell us about you.", "ספר/י לנו עליך.")} align={headingAlign} />

                      <div className="book-form__grid-2">
                        <FormInput
                          label={T("First name", "שם פרטי")}
                          value={firstName}
                          onChange={setFirstName}
                          placeholder={T("Avi", "אבי")}
                          required
                          dir={dir}
                          autoComplete="given-name"
                        />
                        <FormInput
                          label={T("Last name", "שם משפחה")}
                          value={lastName}
                          onChange={setLastName}
                          placeholder={T("Cohen", "כהן")}
                          required
                          dir={dir}
                          autoComplete="family-name"
                        />
                      </div>

                      <MultiField
                        label={T("Email address *", "כתובת אימייל *")}
                        values={emails}
                        setValues={setEmails}
                        type="email"
                        addLabel={T("Add another email", "הוסף/י אימייל נוסף")}
                        placeholder="you@example.com"
                        autoComplete="email"
                      />

                      <MultiField
                        label={T("Phone number (optional)", "מספר טלפון (אופציונלי)")}
                        values={phones}
                        setValues={setPhones}
                        type="tel"
                        addLabel={T("Add another phone", "הוסף/י טלפון נוסף")}
                        placeholder={T("+1 (555) 000-0000", "050-000-0000")}
                        autoComplete="tel"
                      />

                      {/* WhatsApp */}
                      <div className="dyna-modal__field">
                        <label className="dyna-modal__label">
                          {T("WhatsApp number (optional)", "מספר וואטסאפ (אופציונלי)")}
                        </label>
                        <label className="book-form__checkbox-row">
                          <input
                            type="checkbox"
                            checked={waSameAsPhone}
                            onChange={e => setWaSameAsPhone(e.target.checked)}
                          />
                          <span className="book-form__checkbox-label">
                            {T("Same as phone number", "זהה למספר הטלפון")}
                          </span>
                        </label>
                        {!waSameAsPhone && (
                          <MultiField
                            label=""
                            values={whatsapps}
                            setValues={setWhatsapps}
                            type="tel"
                            addLabel={T("Add another WhatsApp", "הוסף/י וואטסאפ נוסף")}
                            placeholder={T("+1 (555) 000-0000", "050-000-0000")}
                            autoComplete="tel"
                          />
                        )}
                      </div>

                      <FormDropdown
                        label={T("Preferred contact method", "אמצעי יצירת קשר מועדף")}
                        value={preferredContact}
                        onChange={setPreferredContact}
                        options={o.preferredContact}
                        otherValue={preferredContactOther}
                        onOtherChange={setPreferredContactOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      {error && <p className="book-form__error">{error}</p>}
                      <NavButtons
                        step={step}
                        canNext={canNext()}
                        submitting={submitting}
                        onBack={goBack}
                        onNext={goNext}
                        rtl={rtl}
                      />
                    </FormSection>
                  </div>
                )}

                {/* ── STEP 2: Your Business ───────────────────────────── */}
                {step === 2 && (
                  <div className="book-form__card">
                    <FormSection label={T("Section 2 — Your Business", "חלק 2 — העסק שלך")}>
                      <Heading text={T("Tell us about your business.", "ספר/י לנו על העסק שלך.")} align={headingAlign} />

                      <FormInput
                        label={T("Business name", "שם העסק")}
                        value={businessName}
                        onChange={setBusinessName}
                        placeholder={T("Your business name", "שם העסק שלך")}
                        required
                        dir={dir}
                        autoComplete="organization"
                      />

                      <FormInput
                        label={T("What does your business do?", "מה העסק שלך עושה?")}
                        value={businessDescription}
                        onChange={setBusinessDescription}
                        placeholder={T("Brief description of what you do and who you serve…", "תיאור קצר של מה שאתה עושה ומי הלקוחות שלך…")}
                        required
                        dir={dir}
                        multiline
                      />

                      <FormInput
                        label={T("Website URL (optional)", "כתובת האתר הנוכחי (אופציונלי)")}
                        value={websiteUrl}
                        onChange={setWebsiteUrl}
                        type="url"
                        placeholder="https://…"
                        dir="ltr"
                        autoComplete="url"
                      />

                      <FormDropdown
                        label={T("Market served", "שוק היעד")}
                        value={market}
                        onChange={setMarket}
                        options={o.market}
                        otherValue={marketOther}
                        onOtherChange={setMarketOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      <FormDropdown
                        label={T("Business stage", "שלב העסק")}
                        value={businessStage}
                        onChange={setBusinessStage}
                        options={o.stage}
                        otherValue={businessStageOther}
                        onOtherChange={setBusinessStageOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      {error && <p className="book-form__error">{error}</p>}
                      <NavButtons
                        step={step}
                        canNext={canNext()}
                        submitting={submitting}
                        onBack={goBack}
                        onNext={goNext}
                        rtl={rtl}
                      />
                    </FormSection>
                  </div>
                )}

                {/* ── STEP 3: What You Need ───────────────────────────── */}
                {step === 3 && (
                  <div className="book-form__card">
                    <FormSection label={T("Section 3 — What You Need", "חלק 3 — מה אתה צריך")}>
                      <Heading text={T("What are you trying to achieve?", "מה אתה מנסה להשיג?")} align={headingAlign} />

                      <FormDropdown
                        label={T("Service interest", "שירות מבוקש")}
                        value={serviceInterest}
                        onChange={setServiceInterest}
                        options={o.service}
                        otherValue={serviceInterestOther}
                        onOtherChange={setServiceInterestOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      <FormInput
                        label={T("Main goal", "מטרה עיקרית")}
                        value={mainGoal}
                        onChange={setMainGoal}
                        placeholder={T(
                          "More leads, better brand presence, rank for a keyword…",
                          "יותר לידים, נוכחות מותגית חזקה יותר, דירוג למילות מפתח…"
                        )}
                        required
                        dir={dir}
                        multiline
                      />

                      <FormDropdown
                        label={T("Budget range", "טווח תקציב")}
                        value={budget}
                        onChange={setBudget}
                        options={o.budget}
                        otherValue={budgetOther}
                        onOtherChange={setBudgetOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      <FormDropdown
                        label={T("Timeline", "לוח זמנים")}
                        value={timeline}
                        onChange={setTimeline}
                        options={o.timeline}
                        otherValue={timelineOther}
                        onOtherChange={setTimelineOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      <FormDropdown
                        label={T("Worked with an agency before?", "עבדת עם סוכנות בעבר?")}
                        value={workedWithAgency}
                        onChange={setWorkedWithAgency}
                        options={o.agency}
                        otherValue={workedWithAgencyOther}
                        onOtherChange={setWorkedWithAgencyOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      {error && <p className="book-form__error">{error}</p>}
                      <NavButtons
                        step={step}
                        canNext={canNext()}
                        submitting={submitting}
                        onBack={goBack}
                        onNext={goNext}
                        rtl={rtl}
                      />
                    </FormSection>
                  </div>
                )}

                {/* ── STEP 4: How You Found Us ────────────────────────── */}
                {step === 4 && (
                  <div className="book-form__card">
                    <FormSection label={T("Section 4 — How You Found Us", "חלק 4 — איך הגעת אלינו")}>
                      <Heading text={T("Almost done.", "כמעט סיימנו.")} align={headingAlign} />

                      <FormDropdown
                        label={T("How did you find us?", "איך הגעת אלינו?")}
                        value={referralSource}
                        onChange={setReferralSource}
                        options={o.referral}
                        otherValue={referralSourceOther}
                        onOtherChange={setReferralSourceOther}
                        dir={dir}
                        rtl={rtl}
                      />

                      {referralSource === "Referral" && (
                        <FormInput
                          label={T("Who referred you?", "מי הפנה אותך?")}
                          value={referralName}
                          onChange={setReferralName}
                          placeholder={T("Name or business", "שם או שם עסק")}
                          dir={dir}
                        />
                      )}

                      <FormInput
                        label={T("Anything else? (optional)", "משהו נוסף? (אופציונלי)")}
                        value={anythingElse}
                        onChange={setAnythingElse}
                        placeholder={T(
                          "Any extra context, questions, or details…",
                          "הקשר נוסף, שאלות, פרטים…"
                        )}
                        dir={dir}
                        multiline
                      />

                      <FileUpload
                        label="Drag or select any files (optional)"
                        labelHe="גרור או בחר קבצים (אופציונלי)"
                        hint="PDF · DOC · images · ZIP · video — up to 100MB each"
                        hintHe="PDF · DOC · תמונות · ZIP · וידאו — עד 100MB"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.txt,.mp4,.mov"
                        multiple
                        maxFiles={10}
                        maxSizeMB={100}
                        category="other"
                        onUpload={setUploadedFiles}
                        existingFiles={uploadedFiles}
                      />

                      {error && <p className="book-form__error">{error}</p>}
                      <NavButtons
                        step={step}
                        canNext={canNext()}
                        submitting={submitting}
                        isSubmit
                        onBack={goBack}
                        onNext={goNext}
                        rtl={rtl}
                      />
                    </FormSection>
                  </div>
                )}

                {/* ── STEP 5: Pick a time (Calendly inline) ──────────── */}
                {step === 5 && (
                  <div className="book-form__card">
                    <FormSection label={T("Section 5 — Book a Call", "חלק 5 — קביעת שיחה")}>
                      {bookingConfirmed ? (
                        <div style={{ textAlign: "center", padding: "32px 16px" }}>
                          <div
                            style={{
                              width: 72,
                              height: 72,
                              borderRadius: "50%",
                              background: "rgba(34, 197, 94, 0.15)",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: 24,
                            }}
                          >
                            <Check size={48} color="rgb(34, 197, 94)" strokeWidth={2.5} />
                          </div>
                          <h1
                            className="book-form__heading"
                            style={{ textAlign: "center", marginBottom: 12 }}
                          >
                            {T("Your call is booked!", "השיחה שלך נקבעה!")}
                          </h1>
                          <p
                            style={{
                              fontFamily: "Geist, system-ui, sans-serif",
                              fontSize: 16,
                              color: "var(--dyna-fg-muted)",
                              marginBottom: 32,
                            }}
                          >
                            {T(
                              "We will send you a confirmation email shortly.",
                              "נשלח לך אישור במייל בקרוב."
                            )}
                          </p>
                          <button
                            className="dyna-btn dyna-btn--primary"
                            onClick={() => navigate("/")}
                          >
                            {T("Back to home", "חזרה לדף הבית")}
                          </button>
                        </div>
                      ) : (
                        <>
                          <Heading
                            text={T("Pick a time that works for you.", "בחר/י זמן שמתאים לך.")}
                            align={headingAlign}
                          />
                          <p
                            style={{
                              fontFamily: "Geist, system-ui, sans-serif",
                              fontSize: 14,
                              color: "var(--dyna-fg-muted)",
                              marginBottom: 16,
                              textAlign: headingAlign,
                            }}
                          >
                            {T(
                              "Choose a slot below — your details are pre-filled.",
                              "בחר/י זמן למטה — הפרטים שלך כבר מולאו."
                            )}
                          </p>
                          {leadId && (
                            <CalendlyEmbed url={CALENDLY_URL} prefill={calendlyPrefill} />
                          )}
                        </>
                      )}
                    </FormSection>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>

          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
